import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, AlertCircle, ChevronDown, ChevronUp, Sparkles, HelpCircle } from 'lucide-react';
import { Service, ServiceCategory, createService, createServiceCategory } from '../lib/services';
import { supabase } from '../lib/supabase';
import CategoryModal from './CategoryModal';
import AddonsLibraryModal from './AddonsLibraryModal';
import { AddonsLibraryItem } from '../lib/addonsLibrary';
import AIServiceCreatorModal from './AIServiceCreatorModal';

interface CreateServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (service: Service) => void;
  businessId?: string;
  categories?: ServiceCategory[];
  onCategoryCreated?: () => void; // Callback to refresh categories
  isMembership?: boolean;
  modalTitle?: string;
  /** Pre-select this category when the modal opens (e.g. "Create service" clicked inside a category card). */
  initialCategoryId?: string;
}

export default function CreateServiceModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  businessId,
  categories = [],
  onCategoryCreated,
  isMembership = false,
  modalTitle = 'Create Service',
  initialCategoryId
}: CreateServiceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isAddonsLibraryModalOpen, setIsAddonsLibraryModalOpen] = useState(false);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAIServiceCreatorOpen, setIsAIServiceCreatorOpen] = useState(false);
  const [compareAtPricing, setCompareAtPricing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // AI description writer (token-metered like the other AI features).
  const [aiDescOpen, setAiDescOpen] = useState(false);
  const [aiDescPrompt, setAiDescPrompt] = useState('');
  const [aiDescGenerating, setAiDescGenerating] = useState(false);
  const [aiDescError, setAiDescError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load the Services app "Compare-at pricing" toggle so the compare-at field
  // only shows when enabled. Stays in sync via the servicesSettingChanged event.
  useEffect(() => {
    if (!isOpen || !businessId || !supabase) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_app_setting', {
        p_business_id: businessId,
        p_app_slug: 'services',
        p_setting_key: 'compare_at_pricing',
        p_default_value: false,
      });
      if (!cancelled && data !== null) setCompareAtPricing(!!data);
    })();
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'compare_at_pricing') setCompareAtPricing(!!detail.value);
    };
    window.addEventListener('servicesSettingChanged', onChange as EventListener);
    return () => { cancelled = true; window.removeEventListener('servicesSettingChanged', onChange as EventListener); };
  }, [isOpen, businessId]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    tax_exempt: false,
    price_options: [
      { name: 'Default', price: '', duration_minutes: '', pricing_type: 'flat' as 'flat' | 'per_unit' | 'per_sqft', unit_price: '', duration_per_unit: '', compare_at_price: '' }
    ],
    add_ons: [] as Array<{
      name: string;
      description: string;
      price: string;
      duration_minutes: string;
    }>
  });

  // Pre-select the caller-provided category whenever the modal opens.
  useEffect(() => {
    if (!isOpen || !initialCategoryId) return;
    const cat = categories.find((c) => c.id === initialCategoryId);
    if (cat) {
      setSelectedCategory(cat);
      setFormData((prev) => ({ ...prev, category_id: cat.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialCategoryId]);

  // Helper function to save current form state with optional overrides
  /** Generate a description with AI — same token flow as the other AI features. */
  const handleGenerateDescription = async () => {
    if (!businessId || !supabase || aiDescGenerating) return;
    if (!aiDescPrompt.trim() && !formData.name.trim()) {
      setAiDescError('Give the AI a hint — a service name or a short prompt.');
      return;
    }
    setAiDescGenerating(true);
    setAiDescError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('ai-write-service-description', {
        body: {
          businessId,
          prompt: aiDescPrompt.trim(),
          serviceName: formData.name.trim(),
          categoryName: selectedCategory?.name || '',
          existingDescription: formData.description.trim(),
        },
      });
      if (fnError) throw new Error(fnError.message || 'Request failed');
      if (!data?.success) {
        setAiDescError(
          data?.insufficientTokens
            ? 'Not enough AI tokens — purchase more in AI Settings to keep using AI features.'
            : data?.error || 'Could not generate a description.',
        );
        return;
      }
      const newFormData = { ...formData, description: String(data.description || '') };
      setFormData(newFormData);
      saveServiceProgress({ formData: newFormData });
      setAiDescOpen(false);
      setAiDescPrompt('');
    } catch (e) {
      setAiDescError(e instanceof Error ? e.message : 'Could not generate a description.');
    } finally {
      setAiDescGenerating(false);
    }
  };

  const saveServiceProgress = (overrides: Record<string, any> = {}) => {
    const serviceData = {
      formData,
      selectedCategory,
      ...overrides // Apply any immediate overrides to handle state timing issues
    };
    console.log('💾 Saving CreateServiceModal progress:', Object.keys(serviceData));
    // Removed all persistence systems
  };

  const isFormValid = () => {
    // Check if all required fields are filled
    const hasCategory = formData.category_id || selectedCategory;
    const hasName = formData.name.trim() !== '';
    const hasDescription = formData.description.trim() !== '';
    
    // Check if all price options are valid
    const hasPriceOptions = formData.price_options.every(option => {
      if (option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft') {
        return option.name.trim() !== '' && option.unit_price !== '' && option.duration_per_unit !== '';
      }
      return option.name.trim() !== '' && option.price !== '' && option.duration_minutes !== '';
    });

    // Check if all add-ons are valid
    const hasValidAddOns = formData.add_ons.every(addon => 
      addon.name.trim() !== '' && 
      addon.description.trim() !== '' && 
      addon.price !== '' && 
      addon.duration_minutes !== ''
    );

    return hasCategory && hasName && hasDescription && hasPriceOptions && hasValidAddOns && !loading;
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Load saved data when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('📥 Loading CreateServiceModal progress...');
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      // Note: Persistence now handled centrally by usePageLifecycle
      // Reset all form state
      setFormData({
        name: '',
        description: '',
        category_id: '',
        tax_exempt: false,
        price_options: [
          { name: 'Default', price: '', duration_minutes: '', pricing_type: 'flat' as 'flat' | 'per_unit' | 'per_sqft', unit_price: '', duration_per_unit: '' }
        ],
        add_ons: []
      });
      setSelectedCategory(null);
      setError(null);
      setIsAddingNewCategory(false);
      setNewCategoryName('');
      setIsDropdownOpen(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) {
      setError('No business selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const categoryId = selectedCategory?.id || formData.category_id;

      const service = await createService(
        {
          business_id: businessId,
          name: formData.name.trim(),
          description: formData.description.trim(),
          category_id: categoryId,
          color: '#000000',
          tax_exempt: formData.tax_exempt,
          tax_rate_id: null,
          is_membership: isMembership,
        },
        formData.price_options.map(option => {
          const isPerUnit = option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft';
          return {
            name: option.name.trim(),
            price: isPerUnit ? 0 : parseFloat(option.price.toString()),
            duration_minutes: isPerUnit ? 0 : parseInt(option.duration_minutes.toString()),
            pricing_type: option.pricing_type || 'flat',
            unit_price: isPerUnit ? parseFloat(option.unit_price.toString()) : 0,
            duration_per_unit: isPerUnit ? parseFloat(option.duration_per_unit.toString()) : 0,
            compare_at_price: option.compare_at_price ? parseFloat(option.compare_at_price.toString()) : null
          };
        }),
        formData.add_ons.map((addon, index) => ({
          name: addon.name.trim(),
          description: addon.description.trim(),
          price: parseFloat(addon.price.toString()),
          duration_minutes: parseInt(addon.duration_minutes.toString()),
          business_id: businessId,
          order_index: index,
          addon_service_id: 'temp_id'
        }))
      );

      console.log('✅ Service created successfully, clearing localStorage');
      // Note: Persistence now handled centrally by usePageLifecycle
      onSuccess(service);
    } catch (error) {
      console.error('Error creating service:', error);
      setError('Failed to create service. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addPriceOption = () => {
    const newFormData = {
      ...formData,
      price_options: [
        ...formData.price_options,
        { name: '', price: '', duration_minutes: '', pricing_type: 'flat' as 'flat' | 'per_unit' | 'per_sqft', unit_price: '', duration_per_unit: '', compare_at_price: '' }
      ]
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const removePriceOption = (index: number) => {
    const newFormData = {
      ...formData,
      price_options: formData.price_options.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const updatePriceOption = (index: number, field: string, value: any) => {
    const newFormData = {
      ...formData,
      price_options: formData.price_options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const addAddOn = () => {
    setIsAddonsLibraryModalOpen(true);
  };

  const handleAddonsSelected = (selectedAddons: AddonsLibraryItem[]) => {
    // Convert selected addons from library to formData format
    const newAddons = selectedAddons.map(addon => ({
      name: addon.name,
      description: addon.description || '',
      price: addon.price.toString(),
      duration_minutes: addon.duration_minutes.toString()
    }));

    const newFormData = {
      ...formData,
      add_ons: [
        ...formData.add_ons,
        ...newAddons
      ]
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
    setIsAddonsLibraryModalOpen(false);
  };

  const removeAddOn = (index: number) => {
    const newFormData = {
      ...formData,
      add_ons: formData.add_ons.filter((_, i) => i !== index)
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  const updateAddOn = (index: number, field: string, value: string | number) => {
    const updatedAddOns = [...formData.add_ons];
    updatedAddOns[index] = {
      ...updatedAddOns[index],
      [field]: value.toString()
    };
    const newFormData = {
      ...formData,
      add_ons: updatedAddOns
    };
    setFormData(newFormData);
    saveServiceProgress({ formData: newFormData });
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleCategoryCreated = async () => {
    // Refresh categories list
    if (onCategoryCreated) {
      await onCategoryCreated();
    }
    setIsCategoryModalOpen(false);
    // The newly created category will be available in the categories prop after refresh
  };

  const handleAddCategory = async () => {
    if (!businessId || !newCategoryName.trim()) return;

    try {
      setLoading(true);
      const newCategory = await createServiceCategory({
        business_id: businessId,
        name: newCategoryName,
        description: '',
        order_index: categories.length,
        is_membership: isMembership
      });

      // Refresh categories list
      if (onCategoryCreated) {
        await onCategoryCreated();
      }
      
      // Select the newly created category
      setSelectedCategory(newCategory);
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setIsAddingNewCategory(false);
      setNewCategoryName('');
      saveServiceProgress({ 
        selectedCategory: newCategory, 
        formData: { ...formData, category_id: newCategory.id }
      });
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;


  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100300,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100310,
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Help Button */}
        <button
          type="button"
          onClick={() => setShowHelp(v => !v)}
          className={`absolute top-4 right-14 h-8 px-2.5 flex items-center gap-1 rounded-full transition-all duration-150 touch-manipulation z-10 text-xs font-semibold ${
            showHelp ? 'bg-blue-100 text-blue-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          style={{ WebkitTapHighlightColor: 'transparent' }}
          title="How creating services works"
        >
          <HelpCircle className="h-4 w-4" />
          Help
          {showHelp ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          <h2 className="text-2xl font-bold text-gray-900" style={{ 
            letterSpacing: '-0.02em'
          }}>
            {modalTitle}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Add a new service to your offerings
          </p>
          
          {/* Create with AI Button */}
          <button
            type="button"
            onClick={() => setIsAIServiceCreatorOpen(true)}
            className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 active:scale-95 transition-all duration-150 shadow-lg hover:shadow-xl"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-semibold">Create with AI</span>
          </button>
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto bg-white"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <form onSubmit={handleSubmit} className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {/* Expandable help — how creating services works */}
            {showHelp && (
              <div className="bg-blue-50/60 border border-blue-200/60 rounded-[12px] p-5 space-y-4 text-[13px] leading-relaxed text-gray-700">
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">How services work</h4>
                  <p>
                    A service is anything a customer can book or be invoiced for. Each service lives in a{' '}
                    <strong>category</strong> (how your menu is organized), has one or more{' '}
                    <strong>price options</strong> (the versions customers choose between), and can offer{' '}
                    <strong>add-ons</strong> (optional extras). The duration you set controls how much time the
                    booking blocks on your calendar.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">1. Category</h4>
                  <p>
                    Pick the group this service belongs to — for example a detailer might use "Inside Detailing",
                    "Outside Detailing", and "Inside &amp; Outside". Categories keep your menu tidy for you and for
                    customers booking online. You can create a new category right from the dropdown.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">2. Name &amp; description</h4>
                  <p>
                    The name is what customers see when booking; the description explains what's included. Both are
                    required — write them the way you'd want them to read on your booking page and invoices. Tick{' '}
                    <strong>Tax Exempt</strong> only if this service shouldn't have your tax rates applied.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">3. Price options</h4>
                  <p className="mb-1.5">
                    Every service needs at least one. Add more with <strong>Add Option</strong> to offer versions of
                    the same service — like "Sedan / SUV / Truck" or "Basic / Premium" — each with its own price and
                    duration. The customer picks one when booking. Three pricing types:
                  </p>
                  <ul className="space-y-1 pl-4 list-disc">
                    <li>
                      <strong>Flat Rate</strong> — one set price and duration (e.g. $150, 2 hours). The right choice
                      for most services.
                    </li>
                    <li>
                      <strong>Per Unit</strong> — price and time multiply by a quantity, like windows or rooms (e.g.
                      $8 and 0.1 hours per window; a 10-window job auto-calculates to $80 and 1 hour).
                    </li>
                    <li>
                      <strong>Per Sq Ft</strong> — same idea but per square foot, for jobs measured by area (e.g.
                      $0.50/sq ft for pressure washing).
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">4. Add-ons (optional)</h4>
                  <p>
                    Extras customers can attach to this service, each with its own price and time — like "Premium
                    Wax" or "Pet Hair Removal". Add-ons are great for upsells: they increase the booking total and
                    extend the calendar time automatically.
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">Tips</h4>
                  <ul className="space-y-1 pl-4 list-disc">
                    <li>Not sure where to start? Hit <strong>Create with AI</strong> and describe the service in plain words.</li>
                    <li>Duration matters — it's what the calendar uses to block time, so estimate realistically.</li>
                    <li>Everything here can be edited later from the Services page, so don't aim for perfect.</li>
                  </ul>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-xl text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              </div>
            )}
            {/* Category Selection Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category *
              </label>
              {!isAddingNewCategory ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 bg-white text-left flex items-center justify-between"
                    style={{ fontSize: '16px' }}
                  >
                    <span className={selectedCategory ? 'text-gray-900' : 'text-gray-400'}>
                      {selectedCategory 
                        ? selectedCategory.name
                        : 'Select a category...'
                      }
                    </span>
                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isDropdownOpen && (
                    <div 
                      className="absolute left-0 right-0 mt-2 max-h-48 overflow-y-auto bg-white rounded-xl shadow-lg border border-gray-200/60"
                      style={{ zIndex: 100100 }}
                    >
                      {categories.map(category => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(category);
                            setFormData(prev => ({ ...prev, category_id: category.id }));
                            setIsDropdownOpen(false);
                            saveServiceProgress({ 
                              selectedCategory: category, 
                              formData: { ...formData, category_id: category.id }
                            });
                          }}
                          className="w-full px-4 py-3 text-left text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors"
                        >
                          {category.name}
                        </button>
                      ))}
                      <div className="border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingNewCategory(true);
                            setIsDropdownOpen(false);
                            setNewCategoryName('');
                          }}
                          className="w-full px-4 py-3 text-left text-sm text-blue-600 hover:bg-blue-50 font-semibold flex items-center"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          + Add category
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200/60">
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter category name"
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      style={{ fontSize: '16px' }}
                      autoFocus
                    />
                    <div className="mt-2 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setIsAddingNewCategory(false);
                          setNewCategoryName('');
                          setSelectedCategory(null);
                        }}
                        className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={!newCategoryName.trim() || loading}
                        className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {loading ? 'Creating...' : 'Create Category'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Basic Information Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      const newFormData = { ...formData, name: e.target.value };
                      setFormData(newFormData);
                      saveServiceProgress({ formData: newFormData });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                    placeholder="Enter service name"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Description *
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setAiDescOpen((v) => !v);
                        setAiDescError(null);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold transition-colors"
                      title="Write the description with AI"
                    >
                      <Sparkles className="h-3 w-3" />
                      AI
                    </button>
                  </div>

                  {aiDescOpen && (
                    <div className="mb-2 rounded-xl border border-blue-200 bg-blue-50/50 p-3 space-y-2">
                      <p className="text-[12px] text-gray-600">
                        Tell the AI what this service includes and it'll write the description
                        {formData.name.trim() ? ` for “${formData.name.trim()}”` : ''}.
                      </p>
                      <div className="flex gap-2">
                        <input
                          value={aiDescPrompt}
                          onChange={(e) => setAiDescPrompt(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleGenerateDescription(); } }}
                          placeholder="e.g. full interior deep clean, shampoo, leather conditioning…"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                        />
                        <button
                          type="button"
                          onClick={() => void handleGenerateDescription()}
                          disabled={aiDescGenerating}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm font-semibold text-white transition-colors disabled:opacity-60"
                        >
                          {aiDescGenerating ? (
                            <>
                              <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              Writing…
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-3.5 w-3.5" />
                              Generate
                            </>
                          )}
                        </button>
                      </div>
                      {aiDescError && <p className="text-[12px] font-medium text-red-600">{aiDescError}</p>}
                    </div>
                  )}

                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => {
                      const newFormData = { ...formData, description: e.target.value };
                      setFormData(newFormData);
                      saveServiceProgress({ formData: newFormData });
                    }}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                    style={{ fontSize: '16px' }}
                    placeholder="Enter service description"
                  />
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.tax_exempt}
                      onChange={(e) => {
                        const newFormData = { ...formData, tax_exempt: e.target.checked };
                        setFormData(newFormData);
                        saveServiceProgress({ formData: newFormData });
                      }}
                      className="h-5 w-5 rounded-xl border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 transition-all duration-150"
                    />
                    <span className="text-sm text-gray-700 font-semibold">Tax Exempt</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Price Options Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Price Options</h3>
              <div className="space-y-4">
                {formData.price_options.map((option, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                    {/* Pricing Type Toggle */}
                    <div className="flex items-center gap-1 mb-4 bg-gray-200/60 rounded-lg p-0.5">
                      {(['flat', 'per_unit', 'per_sqft'] as const).map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updatePriceOption(index, 'pricing_type', type)}
                          className={`flex-1 text-xs font-semibold py-2 px-2 rounded-md transition-all duration-150 ${
                            (option.pricing_type || 'flat') === type
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {type === 'flat' ? 'Flat Rate' : type === 'per_unit' ? 'Per Unit' : 'Per Sq Ft'}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={option.name}
                          onChange={(e) => updatePriceOption(index, 'name', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder={option.pricing_type === 'per_unit' ? 'e.g., Windows' : 'e.g., Basic, Premium'}
                        />
                      </div>

                      {(option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft') ? (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {option.pricing_type === 'per_sqft' ? 'Price / Sq Ft *' : 'Price / Unit *'}
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-sm">$</span>
                              </div>
                              <input
                                type="number"
                                required
                                step="0.01"
                                min="0"
                                value={option.unit_price}
                                onChange={(e) => updatePriceOption(index, 'unit_price', e.target.value)}
                                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }}
                                placeholder={option.pricing_type === 'per_sqft' ? '0.50' : '8.00'}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              {option.pricing_type === 'per_sqft' ? 'Hours / Sq Ft *' : 'Hours / Unit *'}
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              value={option.duration_per_unit}
                              onChange={(e) => updatePriceOption(index, 'duration_per_unit', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                              placeholder="0.1"
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Price *
                            </label>
                            <div className="relative">
                              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <span className="text-gray-500 text-sm">$</span>
                              </div>
                              <input
                                type="number"
                                required
                                step="0.01"
                                value={option.price}
                                onChange={(e) => updatePriceOption(index, 'price', e.target.value)}
                                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Duration (hours) *
                            </label>
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.5"
                              value={option.duration_minutes ? (parseInt(option.duration_minutes) / 60).toString() : ''}
                              onChange={(e) => updatePriceOption(index, 'duration_minutes', Math.round(parseFloat(e.target.value || '0') * 60).toString())}
                              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Compare-at price (optional, gated by Services app setting) */}
                    {compareAtPricing && (
                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Compare at price <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative max-w-[220px]">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={option.compare_at_price}
                            onChange={(e) => updatePriceOption(index, 'compare_at_price', e.target.value)}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Original price shown struck-through.</p>
                      </div>
                    )}

                    {/* Per-unit/sqft summary preview */}
                    {(option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft') && option.unit_price && option.duration_per_unit && (
                      <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-700 font-medium">
                          ${parseFloat(option.unit_price.toString()).toFixed(2)}/{option.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} &middot; {option.duration_per_unit} hrs/{option.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                        </p>
                      </div>
                    )}

                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removePriceOption(index)}
                        className="mt-3 text-xs font-semibold text-red-600 hover:text-red-700 px-3 py-2 rounded-xl hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addPriceOption}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Option
              </button>
            </div>

            {/* Add-ons Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Add-ons</h3>
                <button
                  type="button"
                  onClick={addAddOn}
                  className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 active:bg-blue-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Add-on
                </button>
              </div>
              <div className="space-y-4">
                {formData.add_ons.map((addon, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          value={addon.name}
                          onChange={(e) => updateAddOn(index, 'name', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                          style={{ fontSize: '16px' }}
                          placeholder="e.g., Premium Wax, Extra Coating"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description *
                        </label>
                        <textarea
                          value={addon.description}
                          onChange={(e) => updateAddOn(index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                          style={{ fontSize: '16px' }}
                          placeholder="Describe what this add-on includes"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Price *
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={addon.price || ''}
                              onChange={(e) => updateAddOn(index, 'price', e.target.value)}
                              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Duration (hours) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={addon.duration_minutes ? (parseInt(addon.duration_minutes) / 60).toString() : ''}
                            onChange={(e) => updateAddOn(index, 'duration_minutes', Math.round(parseFloat(e.target.value || '0') * 60).toString())}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeAddOn(index)}
                      className="mt-4 text-xs font-semibold text-red-600 hover:text-red-700 px-3 py-2 rounded-xl hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Remove Add-on
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!isFormValid() || loading}
                className={`w-full inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                  isFormValid() && !loading
                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                    Creating Service...
                  </>
                ) : (
                  modalTitle
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      

      {/* Category Modal */}
      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onSuccess={handleCategoryCreated}
        businessId={businessId}
        mode="create"
        isMembership={isMembership}
      />

      {/* Addons Library Modal */}
      <AddonsLibraryModal
        isOpen={isAddonsLibraryModalOpen}
        onClose={() => setIsAddonsLibraryModalOpen(false)}
        mode="select"
        onSelect={handleAddonsSelected}
      />

      {/* AI Service Creator Modal */}
      <AIServiceCreatorModal
        isOpen={isAIServiceCreatorOpen}
        onClose={() => setIsAIServiceCreatorOpen(false)}
        onServicesCreated={(services) => {
          console.log('📦 Services created callback received:', services.length, 'services');
          // Close AI modal
          setIsAIServiceCreatorOpen(false);
          // Trigger success callback to refresh the services list BEFORE closing CreateServiceModal
          if (services.length > 0) {
            console.log('✅ Calling onSuccess to refresh services list');
            onSuccess(services[0]); // Pass first service to trigger refresh (calls loadData)
          }
          // Small delay then close the CreateServiceModal to ensure refresh happens
          setTimeout(() => {
            handleClose();
          }, 100);
        }}
        onCategoryCreated={onCategoryCreated} // Pass the category refresh callback
        businessId={businessId || ''}
        categories={categories}
      />
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}